import { Box } from "@mittwald/flow-components/dist/components/Box";
import Breakpoints from "@mittwald/flow-components/dist/components/Breakpoints";
import { Button } from "@mittwald/flow-components/dist/components/Button";
import { Form, useForm } from "@mittwald/flow-components/dist/components/Form";
import { TranslationProvider } from "@mittwald/flow-components/dist/components/TranslationProvider";
import invariant from "invariant";
import type { FC } from "react";
import React from "react";
import { v4 as UUIDv4 } from "uuid";
import type { OrderProSpaceInput } from "../../../model/article";
import { ArticleFactory } from "../../../model/article";
import type WebhostingArticle from "../../../model/article/WebhostingArticle";
import Bytes from "../../../model/misc/Bytes";
import OrderPreviewBox from "../../../pages/app/project/modals/CreateProject/components/OrderPreviewBox/OrderPreviewBox";
import { orderWebhostingUrlParam } from "../../implementations/OrderWebhostingWidget/orderWebhostingUrlParam";
import { getPromoCodeFromPath } from "../../utils/promoCode";
import * as Styled from "../styled";
import ConfigurationSections from "./ConfigurationSections";

interface FormFields extends OrderProSpaceInput {
  articleId: string;
  serverId: string;
  useFreeTrial: boolean;
}

interface Props {
  orderWidgetUrl: string;
}
export const ProjectWidgetContent: FC<Props> = (props) => {
  const { orderWidgetUrl } = props;

  const articles: WebhostingArticle[] =
    ArticleFactory.useLoadAllWebhostingArticles();
  const defaultSelectedArticle = articles[0];
  invariant(defaultSelectedArticle, "no project articles available");

  let selectedArticle = defaultSelectedArticle;

  const promotion = getPromoCodeFromPath();

  const form = useForm<FormFields>({
    defaultValues: {
      storageSizeInBytes:
        defaultSelectedArticle.baseStorageAttribute.bytes.bytes,
      articleId: defaultSelectedArticle.id,
      useFreeTrial: true,
    },
    onSubmit: (values) => {
      const storageSizeInGiB = Bytes.of(values.storageSizeInBytes, "bytes").in(
        "GiB",
      );

      const encoded = orderWebhostingUrlParam.encode({
        transactionId: UUIDv4(),
        storageSizeInGiB,
        articleId: values.articleId,
        freeTrial: values.useFreeTrial,
        promotion: promotion?.promoCode,
        promotionDays: promotion?.promotionDays,
      });

      const url = new URL(
        `${window.location.protocol}//${window.location.host}`,
      );
      url.pathname = orderWidgetUrl;
      url.searchParams.set("p", encoded);

      window.location.href = url.toString();
    },
  });

  const [watchedStorageSizeInBytes, watchedArticleId, watchedUseFreeTrial] =
    form.watch(["storageSizeInBytes", "articleId", "useFreeTrial"]);
  const watchedStorageSize = Bytes.of(watchedStorageSizeInBytes, "byte");

  const newSelectedArticle = articles.find((a) => a.id === watchedArticleId);
  if (newSelectedArticle) {
    selectedArticle = newSelectedArticle;
  }

  return (
    <Form controller={form}>
      <Styled.ContentBox>
        <Breakpoints.Box breakpoints={[800]}>
          <Box _flexDirection={["column", "row"]} _itemGap="xl">
            <Box _flexBasis="60%">
              <TranslationProvider
                name="webhostingConfigurationStepContent"
                type="component"
              >
                <ConfigurationSections
                  selectedArticle={selectedArticle}
                  selectedWebhostingArticles={articles}
                />
              </TranslationProvider>
            </Box>
            <Styled.OffsetBox breakpointClassName={["narrow", "wide"]}>
              <Box _alignItems="center" _flexDirection="column" _itemGap="l">
                <OrderPreviewBox
                  freeTrial={watchedUseFreeTrial}
                  promotion={promotion}
                  selectedArticle={selectedArticle}
                  watchedStorageSize={watchedStorageSize}
                />
                <Button action={{ form }} ok text="startOrder" />
              </Box>
            </Styled.OffsetBox>
          </Box>
        </Breakpoints.Box>
      </Styled.ContentBox>
    </Form>
  );
};

export default ProjectWidgetContent;
